/**
 * The customisable dashboard — one page, both products. The local node draws it for its own
 * machine (`/`), the cloud draws it for the selected site (`/site-dashboard`), and everything a
 * card shows, filters, sorts and opens is decided here, so the two cannot drift apart.
 *
 * Every block is a card on the shared `DashboardBoard` (12-column tile grid, 316 px rows). Each
 * card has two sizes: SHRINK shows the headline and its shape over the window, EXPAND keeps that
 * and puts the full list behind it BESIDE it (⅔ or full board width) — searchable, as rows or as
 * picture tiles. "Customize layout" reorders, resizes, hides and restores them, and gives each
 * card its own captions toggle and time range. The layout is remembered per browser.
 *
 * ── What the host supplies ──
 *
 * Everything that differs between the products, and nothing else (`DashboardBoardHost`): how to
 * reach the backend (direct on a node, through the site's tunnel on the cloud), how a snapshot
 * becomes a picture (a URL on a node, a streamed blob on the cloud), the record bodies each app
 * already owns, and the permission keys, which the two products spell differently. Alerts,
 * the watch list and attendance come through `useApi()`, which each app already scopes.
 *
 * ── Where the figures come from ──
 *
 * The headlines are the same reads the dashboard always made, and the reasons behind each
 * survive the redesign:
 *   - the open alerts are the LIVE feed (useLatestAlerts), not a poll, so a new alert lands
 *     here the moment it lands on the board;
 *   - the AI figures are ONE /analytics/summary call per window rather than one per card, split
 *     for today into an alert/presence half that goes out at mount and a per-model half that
 *     waits for the model registry (see `loadCore` / `loadRange`);
 *   - cameras, groups, model assignments and machine load are the host's, polled every 30 s
 *     and paused while the tab is hidden.
 *
 * A card's details list is fetched ONLY while that card is expanded — a shrunk card shows a
 * count and a chart and has no business downloading the hundred rows behind them.
 *
 * Every read is independent and permission-gated: one denied or failed request leaves its own
 * card empty (and named in the banner) instead of blanking the page.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ArrowTrendingDownIcon,
  BellAlertIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  FaceSmileIcon,
  FireIcon,
  HandRaisedIcon,
  ServerStackIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  Squares2X2Icon,
  TruckIcon,
  UserGroupIcon,
  UserIcon,
  UsersIcon,
  ViewfinderCircleIcon,
} from '@heroicons/react/24/outline';
import { type NodeSummaryData, formatUptime } from '../components/dashboard/NodeSummaryCards';
import { type AiAnalyticsSummary } from '../components/dashboard/AiAnalyticsCards';
import { type AlertPlace } from '../components/dashboard/LatestAlertsStrip';
import { type HexAlert, type WatchlistDetection } from '../api/contracts';
import { default as CameraStatusDonut } from '../components/dashboard/CameraStatusDonut';
import { default as Modal } from '../components/common/Modal';
import { default as RecordPager } from '../components/common/RecordPager';
import { useActiveModelSurfaces } from '../hooks/useActiveModelSurfaces';
import { useApi } from '../api/ApiProvider';
import { loadEnrolledPhoto } from '../components/people/useEnrolledPhoto';
import { reportModelBySlug, reportModelLabel, reportModelSlug } from '../constants/reportModels';
import { EmployeeDetailBody } from './attendance/EmployeeDetail';
import { useLatestAlerts } from '../hooks/useLatestAlerts';
import { describeApiError } from '../api/apiError';
import { hasSeries, seriesMax } from '../components/dashboard/MiniCharts';
import { alertName } from '../components/alerts/alertVocab';
import { detectionImageKey, watchListHref, watchlistCategoryOf, PERSON_WATCHLIST_CATEGORIES, VEHICLE_WATCHLIST_CATEGORIES } from '../components/common/watchlistForm';
import {
  DashboardBoard,
  useBoardLayout,
  useBoardCard,
  useCardFilter,
  BoardFilterChips as FilterChips,
  type BoardCardDef,
  type BoardCatalogEntry,
  type BoardRange,
  BoardCardBody,
  CardHead,
  CardSection,
  CardFoot,
  CardSkeleton,
  BoardChip as Chip,
  BoardHero as Hero,
  BarsPlot,
  BarsOrQuiet,
  RingStat,
  BoardQuiet as Quiet,
  BoardMeter as Meter,
  BOARD_TONE as TONE,
  BOARD_SUNK as SUNK,
  BOARD_FOCUS as FOCUS,
  boardTint as tint,
  boardFmt as fmt,
  windowCaption,
  windowSuffix,
  perBucket,
  DetailsPanel,
  type DetailItem,
  AlertTimeline,
  severityColor,
  severityLabel,
  spanLabel,
  alertSeconds,
} from '../components/dashboard/board';

/** How many rows an expanded card pulls. A dashboard list, not a report — the arrow opens that. */
const DETAIL_LIMIT = 100;

/** Normalise the assorted `{data}` / `{data,pagination}` / bare-array response shapes. */
function toArray(res: any): any[] {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

// ─── The host ────────────────────────────────────────────────────────────────

/** A row's picture: a URL the browser can load, or a lazy loader for one it cannot. */
export interface DashboardPicture {
  image?: string | null;
  loadImage?: () => Promise<string | null>;
}

export interface DashboardMachineRead {
  cameras: any[];
  groups: any[];
  assignments: any;
  /** The cloud node-registry's metric names (cpuPercent, memoryPercent, storageUsedGB…). */
  metrics: any;
  /** A line qualifying the meters — "3 machines · memory and disk are totals…". */
  note?: string | null;
  /** The reads that failed, named for the banner ("cameras", "groups"…). */
  failed: string[];
}

/** Everything the dashboard needs that differs between the local node and the cloud. */
export interface DashboardBoardHost {
  /** localStorage key for the card layout. Versioned by the caller. */
  layoutKey: string;
  /**
   * What the page is ABOUT, for the hooks that are scoped by it — the cloud's selected site.
   * Omitted on a node, which is only ever about itself. Remount the page (React `key`) when it
   * changes, so no figure from the previous site survives the switch.
   */
  scopeKey?: string;
  /** The page heading. */
  title: ReactNode;
  subtitle?: ReactNode;
  /** "this machine" / "this site" — in the sentences that name the subject. */
  subjectNoun: string;
  can: {
    cameras: boolean;
    groups: boolean;
    models: boolean;
    assignModels: boolean;
    alerts: boolean;
    monitor: boolean;
    aiEvents: boolean;
    attendance: boolean;
  };
  /** Where the System card's arrow and status tile go. */
  monitorPath?: string;
  loadMachine: () => Promise<DashboardMachineRead>;
  getSummary: (surfaces: string[], opts: {
    alerts?: boolean;
    attendance?: boolean;
    watchlist?: boolean;
    range?: BoardRange;
    fresh?: boolean;
  }) => Promise<AiAnalyticsSummary>;
  /** `model` is the command-center's model key (`fire_smoke`, `person_count`). */
  getModelEvents: (model: string, params: Record<string, any>) => Promise<any>;
  getModelEvent: (model: string, id: string) => Promise<any>;
  getModelStats: (model: string, params: Record<string, any>) => Promise<any>;
  /**
   * An event's picture from one of its `image_urls` entries: `key` is the entry's name
   * (`faceCrop`), `ref` its stored value. A node resolves `ref` through its snapshot proxy; the
   * cloud streams `key` through the site's tunnel.
   */
  picture: (args: { model: string; eventId: string; key: string; ref: string }) => DashboardPicture;
  /** The app's own event record body (it owns the gallery and lightbox). */
  EventRecord: ComponentType<{
    event: any;
    model: string;
    cameraNameMap: Record<string, string>;
    personName?: string;
  }>;
  /** The app's own alert record body. */
  AlertRecord: ComponentType<{ alertId: string }>;
}

// ─── Small formatting helpers ────────────────────────────────────────────────

const clock = (sec?: number | null) =>
  sec ? new Date(sec * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';

const isoClock = (iso?: string | null) => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? clock(ms / 1000) : null;
};

const initials = (name?: string | null) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';

/** A similarity or confidence as "94% match" — the AI module sends 0..1, older rows 0..100. */
const pct = (v: any) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${Math.round(n <= 1 ? n * 100 : n)}%`;
};

/** Stable colour for a person/thing badge, from its name — so "RK" is the same blue everywhere. */
const BADGE_COLORS = ['#E8491A', '#0EA5E9', '#8B5CF6', '#14B8A6', '#F43F5E', '#64748B', '#F59E0B', '#22C55E'];
const badgeColor = (seed: string) => {
  let h = 0;
  for (const c of seed) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return BADGE_COLORS[h % BADGE_COLORS.length];
};

/** The vocabulary's own hues (watchlistForm chips): red barred, violet wanted/stolen, sky missing. */
const CATEGORY_TONE: Record<string, string> = {
  blacklisted: '#ef4444',
  wanted: '#8b5cf6',
  stolen: '#8b5cf6',
  missing: '#0ea5e9',
};

/** Which of an event's pictures a row shows, best first. */
const IMAGE_PREFERENCE: Record<string, string[]> = {
  face: ['faceCrop', 'alignedFace', 'personCrop', 'fullFrame', 'snapshot'],
  default: ['fullFrame', 'full_frame', 'snapshot', 'personCrop', 'objectCrop', 'crop'],
};

/** An event row's best picture, through the host (a URL on a node, a tunnel fetch on the cloud). */
function eventPicture(host: DashboardBoardHost, ev: any, model: string, prefer = 'default'): DashboardPicture {
  const eventId = String(ev?.id || ev?.event_id || '');
  if (!eventId) return {};
  const pick = (v: any) => {
    if (!v) return null;
    if (typeof v === 'string') { try { v = JSON.parse(v); } catch { return null; } }
    return v && typeof v === 'object' ? (v as Record<string, string>) : null;
  };
  const urls = pick(ev?.image_urls) || pick(ev?.image_paths);
  if (urls) {
    const keys = Object.keys(urls).filter((k) => urls[k]);
    const flat = (k: string) => k.toLowerCase().replace(/[^a-z0-9]/g, '');
    const hit = (IMAGE_PREFERENCE[prefer] || IMAGE_PREFERENCE.default)
      .map((want) => keys.find((k) => flat(k) === flat(want)))
      .find(Boolean) || keys[0];
    if (hit) return host.picture({ model, eventId, key: hit, ref: urls[hit] });
  }
  return ev?.snapshot_path ? host.picture({ model, eventId, key: 'snapshot', ref: ev.snapshot_path }) : {};
}

/** A picture as one loadable URL — for a loader that has to hand back a single answer. */
async function pictureUrl(p: DashboardPicture): Promise<string | null> {
  if (p.image) return p.image;
  return p.loadImage ? p.loadImage() : null;
}

/** An event's time as a sortable number — rows carry epoch seconds, ms, or ISO strings. */
function sortTime(v: any): number {
  const n = Number(v);
  if (Number.isFinite(n) && n > 0) return n;
  const t = Date.parse(v);
  return Number.isFinite(t) ? t / 1000 : 0;
}

/** Newest first — the default order of every event list on the dashboard. */
function newestFirst<T>(rows: T[], at: (r: T) => any): T[] {
  return [...rows].sort((a, b) => sortTime(at(b)) - sortTime(at(a)));
}

/** How many items fall in each group, in first-seen order — the chips over a card's list. */
function groupCounts<T>(rows: T[], keyOf: (r: T) => string) {
  const out = new Map<string, number>();
  rows.forEach((r) => { const k = keyOf(r); out.set(k, (out.get(k) || 0) + 1); });
  return out;
}

/**
 * A camera's most recent event frame for `model` — the picture for a per-camera row (person
 * count, people detected), whose rows are aggregates with no picture of their own. Fetched
 * lazily, one row at a time as it scrolls into view.
 */
async function latestCameraFrame(host: DashboardBoardHost, model: string, cameraId: string): Promise<string | null> {
  try {
    const rows = toArray(await host.getModelEvents(model, { camera_id: cameraId, limit: 1 }));
    return rows[0] ? await pictureUrl(eventPicture(host, rows[0], model)) : null;
  } catch {
    return null;
  }
}

/**
 * What a details row opens: an AI event, by id — or, for a per-camera row (person count,
 * people detected), that camera's latest event of the model, the same frame the row shows.
 * `model` is the command-center's model KEY (`fire_smoke`), not the URL slug.
 */
interface EventRef {
  model: string;
  id?: string;
  cameraId?: string;
  title: string;
  /** The identity the ROW showed, so the record cannot contradict the row that opened it. */
  personName?: string;
}

/** A dashboard surface key (`fire-smoke`) as a model key (`fire_smoke`). */
const modelKey = (surface: string) => reportModelBySlug(surface)?.key || surface;

/** One event's record inside the dashboard's modal — the body the reports and watch list use. */
function DashboardEventRecord({ host, eventRef, cameraNameMap, onLoaded }: {
  host: DashboardBoardHost;
  eventRef: EventRef;
  cameraNameMap: Record<string, string>;
  onLoaded: (eventId: string | null) => void;
}) {
  const [event, setEvent] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setEvent(null);
    const load: Promise<any> = eventRef.id
      ? host.getModelEvent(eventRef.model, eventRef.id)
      : host
        .getModelEvents(eventRef.model, { camera_id: eventRef.cameraId, limit: 1 })
        .then((res) => toArray(res)[0] || null);
    load
      .then((ev: any) => {
        if (!alive) return;
        setEvent(ev || null);
        onLoaded(ev ? String(ev.event_id || ev.id || eventRef.id || '') || null : null);
      })
      .catch(() => { if (alive) { setEvent(null); onLoaded(null); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventRef.model, eventRef.id, eventRef.cameraId]);

  if (loading) return <div className="p-6"><div className="h-64 animate-pulse rounded-lg bg-muted" /></div>;
  if (!event) {
    return (
      <div className="py-16 text-center text-sm text-muted-foreground">
        {eventRef.id
          ? 'That event is no longer available — it may have been deleted or passed its retention window.'
          : 'This camera has no event to show yet.'}
      </div>
    );
  }
  return (
    <host.EventRecord
      event={event}
      model={eventRef.model}
      cameraNameMap={cameraNameMap}
      personName={eventRef.personName}
    />
  );
}

/** The window a range covers, computed locally — used only until the node's own one arrives. */
function localWindow(range: BoardRange) {
  const to = Math.floor(Date.now() / 1000);
  if (range === '24h') return { from: to - 86400, to, label: '24h' };
  if (range === '7d') return { from: to - 7 * 86400, to, label: '7d' };
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  return { from: Math.floor(midnight.getTime() / 1000), to, label: 'today' };
}

// ─── Lazy details ────────────────────────────────────────────────────────────

interface DetailsState<T> {
  items: T[];
  loading: boolean;
  error: string | null;
}

/**
 * A card's details list, fetched only while `enabled` (its card is expanded) and refetched when
 * `key` (its window) changes or `tick` (the page poll) advances. A failed refetch keeps the rows
 * already on screen — one bad poll must not empty a list the operator is reading.
 */
function useDetails<T>(enabled: boolean, key: string, tick: number, fetcher: () => Promise<T[]>): DetailsState<T> {
  const [state, setState] = useState<DetailsState<T> & { key: string }>({ items: [], loading: false, error: null, key: '' });
  const fetchRef = useRef(fetcher);
  fetchRef.current = fetcher;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setState((s) => (s.key === key ? { ...s, loading: true } : { items: [], loading: true, error: null, key }));
    fetchRef.current()
      .then((items) => { if (!cancelled) setState({ items, loading: false, error: null, key }); })
      .catch((err) => { if (!cancelled) setState((s) => ({ ...s, loading: false, error: describeApiError(err) })); });
    return () => { cancelled = true; };
  }, [enabled, key, tick]);

  return state;
}

// ─── Model surfaces with a generic card ──────────────────────────────────────

/**
 * Detectors that have no bespoke card. Each still gets one — a model with nothing to report
 * shows ZERO rather than vanishing (see AiAnalyticsCards rule 2) — built from the same parts.
 */
const DETECTOR_CARDS: Record<string, {
  title: string;
  icon: typeof FireIcon;
  unit: string;
  tone: string;
  quiet: string;
  report: string;
}> = {
  intrusion: { title: 'Intrusions', icon: ExclamationTriangleIcon, unit: 'detections', tone: TONE.crit, quiet: 'No intrusions', report: '/reports/intrusion' },
  'fire-smoke': { title: 'Fire & Smoke', icon: FireIcon, unit: 'detections', tone: TONE.crit, quiet: 'No fire or smoke detected', report: '/reports/fire-smoke' },
  'ppe-violation': { title: 'PPE Violations', icon: ShieldCheckIcon, unit: 'violations', tone: TONE.warn, quiet: 'No PPE violations', report: '/reports/ppe-violation' },
  'vehicle-crossing': { title: 'Vehicles', icon: TruckIcon, unit: 'crossings', tone: TONE.brand, quiet: 'No crossings', report: '/reports/vehicle-crossing' },
  theft: { title: 'Theft', icon: HandRaisedIcon, unit: 'detections', tone: TONE.crit, quiet: 'No theft detected', report: '/reports/theft' },
  fall: { title: 'Falls', icon: ArrowTrendingDownIcon, unit: 'detections', tone: TONE.crit, quiet: 'No falls detected', report: '/reports/fall' },
};

const DETECTOR_KEYS = Object.keys(DETECTOR_CARDS);

// ─── Alert severity boxes ────────────────────────────────────────────────────

type Sev = 'all' | 'critical' | 'warning' | 'normal';

function SeverityBoxes({ counts, active, onPick }: {
  counts: { critical: number; warning: number; normal: number } | null;
  active: Sev;
  onPick: (s: Sev) => void;
}) {
  const boxes: Array<{ key: Exclude<Sev, 'all'>; label: string }> = [
    { key: 'critical', label: 'Critical' },
    { key: 'warning', label: 'Warning' },
    { key: 'normal', label: 'Info' },
  ];
  return (
    <div className="grid flex-none grid-cols-3 gap-2">
      {boxes.map((b) => {
        const color = severityColor(b.key);
        const on = active === b.key;
        return (
          <button
            key={b.key}
            type="button"
            aria-pressed={on}
            onClick={() => onPick(on ? 'all' : b.key)}
            title={on ? 'Show every severity' : `Only ${b.label.toLowerCase()} alerts`}
            className={`rounded-[10px] px-2.5 py-1.5 text-left transition-shadow ${FOCUS}`}
            style={{ backgroundColor: on ? tint(color, 12) : SUNK, boxShadow: on ? `inset 0 0 0 1.5px ${color}` : undefined }}
          >
            <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} aria-hidden />
              {b.label}
            </span>
            <span className="block text-[18px] font-bold leading-tight tabular-nums" style={{ color }}>
              {counts ? fmt(counts[b.key]) : '—'}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export interface DashboardBoardPageProps {
  host: DashboardBoardHost;
}

export default function DashboardBoardPage({ host }: DashboardBoardPageProps) {
  const navigate = useNavigate();
  const { hexAlerts, watchlist: watchlistApi, attendance: attendanceApi } = useApi();
  // The host is a fresh object every render; its functions are read through a ref so the loads
  // below depend on what actually changes (permissions), not on the parent re-rendering.
  const hostRef = useRef(host);
  hostRef.current = host;

  const [data, setData] = useState<NodeSummaryData>({
    cameras: [],
    groups: [],
    assignments: {},
    alerts: [],
    metrics: {},
    loading: true,
  });
  const [statusNote, setStatusNote] = useState<string | null>(null);
  const [failed, setFailed] = useState<string[]>([]);
  const [editing, setEditing] = useState(false);
  /** Bumped by the page poll — what refetches the expanded cards' lists. */
  const [tick, setTick] = useState(0);
  const [sev, setSev] = useState<Sev>('all');

  const canCameras = host.can.cameras;
  const canGroups = host.can.groups;
  const canModels = host.can.models;
  const canAlerts = host.can.alerts;
  const canMonitor = host.can.monitor;
  // The analytics figures are gated by one key. The command-center has no per-user camera
  // scoping (service auth only), so these figures are SITE-WIDE for anyone who may see AI
  // events at all — which is what the cards present them as.
  const canAiEvents = host.can.aiEvents;
  const canAttendance = host.can.attendance;
  const showMachine = canMonitor || canCameras || canModels || canGroups;

  // Which model cards to draw. `surfaces` is null until the registry is known.
  const { surfaces } = useActiveModelSurfaces(host.scopeKey);
  const active = useMemo(() => new Set(surfaces || []), [surfaces]);

  /**
   * The open alerts, LIVE — the same adapter the alert board uses, so a new alert lands here
   * the moment it lands there, and acknowledging one from either place clears it in both.
   */
  const latest = useLatestAlerts({ api: hexAlerts, blocked: !canAlerts, nodeKey: host.scopeKey });

  // ── The card catalogue: what this operator, on this node, can have on the board ──

  const catalog = useMemo<BoardCatalogEntry[]>(() => {
    const c: BoardCatalogEntry[] = [];
    if (canAlerts) c.push({ id: 'alerts', defaultView: 'cards' });
    if (canAiEvents) c.push({ id: 'peopleWatch' }, { id: 'vehicleWatch' });
    if (canAiEvents && canAttendance) c.push({ id: 'presence' });
    if (canAiEvents && active.has('person-count')) c.push({ id: 'personCount' });
    if (canAiEvents && active.has('face')) c.push({ id: 'faces' });
    if (canAiEvents && active.has('person')) c.push({ id: 'peopleDetected' });
    if (showMachine) c.push({ id: 'system' });
    if (canAiEvents) {
      Object.keys(DETECTOR_CARDS).forEach((k) => active.has(k) && c.push({ id: `surface:${k}` }));
    }
    return c;
  }, [canAlerts, canAiEvents, canAttendance, active, showMachine]);

  const layout = useBoardLayout(host.layoutKey, catalog);
  const itemOf = useCallback((id: string) => layout.items.find((it) => it.id === id), [layout.items]);
  const expanded = (id: string) => {
    const it = itemOf(id);
    return !!it && !it.hidden && it.size === 'expand';
  };
  const rangeOf = (id: string): BoardRange => itemOf(id)?.range || 'today';

  // ── Backend reads: cameras, groups, assignments, machine load ──

  const load = useCallback(async () => {
    const m = await hostRef.current.loadMachine();
    setStatusNote(m.note || null);
    setData({
      cameras: m.cameras || [],
      groups: m.groups || [],
      assignments: m.assignments || {},
      alerts: [],
      metrics: m.metrics || {},
      loading: false,
    });
    // Named, because "no cameras" and "the backend didn't answer" look identical on a card.
    setFailed((prev) => [...prev.filter((f) => f.startsWith('AI ')), ...m.failed]);
  }, [canCameras, canGroups, canModels, canMonitor]);

  useEffect(() => { load(); }, [load]);

  // ── AI summaries ──
  //
  // `core` is TODAY's alert, presence and watch-list figures — no model list needed, so it goes
  // out at mount. `ranged[r]` is the per-model figures for window `r` (plus, for the rolling
  // windows, that window's watch-list figures). One request per window IN USE: a board where
  // every card is on "today" makes exactly the two calls the old dashboard did.

  const [core, setCore] = useState<AiAnalyticsSummary | null>(null);
  const [ranged, setRanged] = useState<Partial<Record<BoardRange, AiAnalyticsSummary>>>({});

  const noteAiFailure = useCallback((label: string, err: unknown) => {
    // Previous figures stay on screen; the reason is named so a 403 from the command-center's
    // service auth doesn't read the same as a genuinely empty day.
    const reason = `${label} (${describeApiError(err)})`;
    setFailed((prev) => (prev.some((f) => f.startsWith(label)) ? prev : [...prev, reason]));
  }, []);

  const loadCore = useCallback(async (fresh = false) => {
    if (!canAiEvents) return;
    try {
      setCore(await hostRef.current.getSummary([], { alerts: canAlerts, attendance: canAttendance, fresh }));
    } catch (err) {
      noteAiFailure('AI summary', err);
    }
  }, [canAiEvents, canAlerts, canAttendance, noteAiFailure]);

  /**
   * Today's per-model figures wait for the registry — asking before then would either miss
   * models or request every one. The rolling windows go out regardless, because they also
   * carry their window's watch-list figures, which need no model list.
   */
  const loadRange = useCallback(async (range: BoardRange, fresh = false) => {
    if (!canAiEvents || surfaces === null) return;
    if (range === 'today' && surfaces.length === 0) return;
    try {
      const res = await hostRef.current.getSummary(surfaces, {
        alerts: false,
        attendance: false,
        watchlist: range !== 'today',
        range,
        fresh,
      });
      setRanged((prev) => ({ ...prev, [range]: res }));
    } catch (err) {
      noteAiFailure('AI model figures', err);
    }
  }, [canAiEvents, surfaces, noteAiFailure]);

  /** Every window some visible, ranged card is on — plus today, which the per-model cards default to. */
  const rangesInUse = useMemo(() => {
    const set = new Set<BoardRange>(['today']);
    layout.items.forEach((it) => {
      if (!it.hidden && (it.id === 'peopleWatch' || it.id === 'vehicleWatch' || it.id === 'personCount'
        || it.id === 'faces' || it.id === 'peopleDetected' || it.id.startsWith('surface:'))) {
        set.add(it.range);
      }
    });
    return [...set].sort();
  }, [layout.items]);
  const rangesKey = rangesInUse.join(',');
  const rangesRef = useRef(rangesInUse);
  rangesRef.current = rangesInUse;

  useEffect(() => { loadCore(); }, [loadCore]);
  useEffect(() => {
    rangesRef.current.forEach((r) => loadRange(r));
  }, [loadRange, rangesKey]);

  /**
   * The counts follow the FEED, not the clock: when the live alert feed moves, the aggregates
   * are re-read (debounced, `fresh`) instead of waiting up to thirty seconds for the next tick.
   */
  const alertRevision = latest.revision;
  useEffect(() => {
    if (!alertRevision) return;
    const id = setTimeout(() => {
      loadCore(true);
      rangesRef.current.forEach((r) => loadRange(r, true));
    }, 1500);
    return () => clearTimeout(id);
  }, [alertRevision, loadCore, loadRange]);

  // 30 s, paused while the tab is hidden — a background dashboard polling costs the node real work.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.hidden) return;
      load();
      loadCore();
      rangesRef.current.forEach((r) => loadRange(r));
      latest.refresh();
      setTick((t) => t + 1);
    }, 30_000);
    return () => clearInterval(id);
  }, [load, loadCore, loadRange, latest.refresh]);

  const refreshAll = () => {
    // `fresh` here and nowhere else: Refresh must recompute, or it hands back the same cached
    // figures the operator just decided were not good enough.
    load();
    loadCore(true);
    rangesInUse.forEach((r) => loadRange(r, true));
    latest.refresh();
    setTick((t) => t + 1);
  };

  /** One window's figures, from whichever request carries them. */
  const figures = (range: BoardRange) => {
    const r = ranged[range];
    const loadedSurfaces = range === 'today' ? ranged.today : r;
    return {
      window: (range === 'today' ? core?.window || r?.window : r?.window) || localWindow(range),
      surfaces: loadedSurfaces?.surfaces || null,
      watchlist: range === 'today' ? core?.watchlist ?? null : r?.watchlist ?? null,
      watchlistLoading: range === 'today' ? !core : !r,
      surfacesLoading: surfaces === null || (surfaces.length > 0 && !loadedSurfaces),
    };
  };

  // ── Lookups the cards share ──

  /** camera id → name, site. The site is the ROOT of the camera's primary group tree. */
  const alertPlaces = useMemo(() => {
    const byId: Record<string, any> = {};
    (data.groups || []).forEach((g: any) => g?.id && (byId[g.id] = g));
    const rootOf = (groupId: string): string => {
      let cur = groupId;
      const seen = new Set<string>();
      while (byId[cur]?.parent_id && !seen.has(cur)) {
        seen.add(cur);
        cur = byId[cur].parent_id;
      }
      return cur;
    };
    const places: Record<string, AlertPlace> = {};
    (data.cameras || []).forEach((c: any) => {
      if (!c?.id) return;
      const groupId = c.groups?.[0]?.id ?? null;
      const site = groupId ? byId[rootOf(groupId)]?.name : undefined;
      places[c.id] = { name: c.name, site: site && site !== c.name ? site : undefined };
    });
    return places;
  }, [data.cameras, data.groups]);

  const camName = (id?: string | null) => (id ? alertPlaces[id]?.name || `#${String(id).slice(0, 8)}` : '—');
  const camSite = (id?: string | null) => (id ? alertPlaces[id]?.site || '' : '');

  // ── Event record modal ──
  //
  // A details row opens its event over the dashboard, as an alert does. The pager walks the
  // rows the list was SHOWING when the row was clicked — filtered and searched — not the whole
  // feed behind them.
  const [eventView, setEventView] = useState<{ refs: EventRef[]; index: number } | null>(null);
  const [eventViewId, setEventViewId] = useState<string | null>(null);
  const viewingEvent = eventView ? eventView.refs[eventView.index] || null : null;
  const openEventRow = useCallback((item: DetailItem, shown: DetailItem[]) => {
    const rows = shown.filter((it) => !!it.data);
    const index = rows.findIndex((it) => it.id === item.id);
    if (index < 0) return;
    setEventViewId(null);
    setEventView({ refs: rows.map((it) => it.data as EventRef), index });
  }, []);

  // A presence row is a person, not an event: it opens the person's day — the same record the
  // presence board's drawer shows — in the same frame and pager as the event records.
  const [employeeView, setEmployeeView] = useState<{
    rows: Array<{ id: string; name: string; sub?: string }>;
    index: number;
    date?: string;
  } | null>(null);
  const viewingEmployee = employeeView ? employeeView.rows[employeeView.index] || null : null;
  const openEmployeeRow = useCallback((date?: string) => (item: DetailItem, shown: DetailItem[]) => {
    const index = shown.findIndex((it) => it.id === item.id);
    if (index < 0) return;
    setEmployeeView({ rows: shown.map((it) => ({ id: it.id, name: it.title, sub: it.sub })), index, date });
  }, []);

  const cameraNameMap = useMemo(
    () => Object.fromEntries(Object.entries(alertPlaces).map(([id, p]) => [id, p.name || ''])),
    [alertPlaces],
  );

  // ── Alert record modal ──
  //
  // Opened over the dashboard rather than routed to: this page is a vantage point, and routing
  // to /alerts/:id puts the Back button on the alert list. Held as a CURSOR into the feed, which
  // gives the modal its pager.
  const [viewingIndex, setViewingIndex] = useState<number | null>(null);
  const viewable = useMemo(() => latest.alerts.filter((a) => !!a.id), [latest.alerts]);
  const viewing = viewingIndex == null ? null : viewable[Math.min(viewingIndex, viewable.length - 1)] || null;
  const openAlert = (a: HexAlert) => {
    // No id: the node broadcast it before its storage lane wrote the row — nothing to open yet.
    if (!a.id) { navigate(`/alerts?camera_id=${a.cameraId}`); return; }
    const at = viewable.findIndex((v) => v.id === a.id);
    setViewingIndex(at >= 0 ? at : 0);
  };

  const filteredAlerts = useMemo(
    () => (sev === 'all' ? latest.alerts : latest.alerts.filter((a) => a.severity === sev)),
    [latest.alerts, sev],
  );

  // ── Details lists (fetched only while their card is expanded) ──

  const watchDetails = (subject: 'person' | 'vehicle', range: BoardRange) => async () => {
    const w = figures(range).window;
    const res = await watchlistApi.detections({ subject, from: w.from, to: Math.floor(Date.now() / 1000), limit: DETAIL_LIMIT });
    return res.data || [];
  };
  const peopleWatchRows = useDetails<WatchlistDetection>(
    expanded('peopleWatch'), `p:${rangeOf('peopleWatch')}`, tick, watchDetails('person', rangeOf('peopleWatch')),
  );
  const vehicleWatchRows = useDetails<WatchlistDetection>(
    expanded('vehicleWatch'), `v:${rangeOf('vehicleWatch')}`, tick, watchDetails('vehicle', rangeOf('vehicleWatch')),
  );
  const presenceRows = useDetails<any>(expanded('presence'), 'presence', tick, async () => {
    const res = await attendanceApi.today();
    return res?.employees || [];
  });
  const faceRows = useDetails<any>(expanded('faces'), `f:${rangeOf('faces')}`, tick, async () => {
    const w = figures(rangeOf('faces')).window;
    const res = await hostRef.current.getModelEvents('face', { from: w.from, limit: DETAIL_LIMIT });
    return toArray(res);
  });
  const detectionRows = useDetails<{ camera_id: string; count: number }>(
    expanded('peopleDetected'), `d:${rangeOf('peopleDetected')}`, tick, async () => {
      const w = figures(rangeOf('peopleDetected')).window;
      // [by sub-type, by camera (top 20), total] — see PersonEvent.getStats.
      const stats = await hostRef.current.getModelStats('person', { from: w.from });
      const byCamera = Array.isArray(stats) ? stats[1] : [];
      return (byCamera || []).map((r: any) => ({ camera_id: r.camera_id, count: Number(r.count || 0) }));
    },
  );
  // One per generic detector. DETECTOR_KEYS is a module constant, so this loop calls the same
  // hooks in the same order on every render.
  const detectorRows: Record<string, DetailsState<any>> = {};
  for (const k of DETECTOR_KEYS) {
    const id = `surface:${k}`;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    detectorRows[k] = useDetails<any>(expanded(id), `s:${k}:${rangeOf(id)}`, tick, async () => {
      const w = figures(rangeOf(id)).window;
      return toArray(await hostRef.current.getModelEvents(modelKey(k), { from: w.from, limit: DETAIL_LIMIT }));
    });
  }

  // ── Card renderers ──
  //
  // PLAIN FUNCTIONS, not components declared in this body: a component type created on every
  // render would remount its card on every poll, throwing away a search the operator is typing.
  // The board calls each one inside its own stable per-card component, below the card's context
  // provider, so the useBoardCard() calls in them are ordinary, stable hooks of that component.

  const go = (path: string) => () => navigate(path);

  const renderAlerts = () => {
    const { view, split } = useBoardCard();
    const counts = core?.alerts?.open || null;
    const open = latest.open || counts?.total || 0;
    const head = (
      <CardHead
        icon={BellAlertIcon}
        title="Alert Timeline"
        chip={open > 0 ? <Chip onClick={() => navigate('/alerts?status=new')} title="Open the alert list">{fmt(open)} open</Chip> : undefined}
        onOpen={() => navigate('/alert-board')}
        openLabel="Open the alert board"
      />
    );

    const pulse = core?.alerts?.pulse;
    const newest = filteredAlerts[0];
    const newestSec = newest ? alertSeconds(newest) : null;
    const items: DetailItem[] = filteredAlerts.map((a, i) => {
      const sec = alertSeconds(a);
      return {
        id: a.id || `${a.cameraId}-${i}`,
        title: a.alertName || 'Alert',
        sub: [severityLabel(a.severity), a.description].filter(Boolean).join(' · '),
        image: a.snapshotUrl || null,
        // The cloud's alerts carry no loadable URL — the picture is streamed through the
        // site's tunnel by the alert adapter, once the node says it is stored.
        loadImage: !a.snapshotUrl && hexAlerts.loadPreview ? () => hexAlerts.loadPreview!(a) : undefined,
        previewReady: a.previewReady,
        c2: camName(a.cameraId),
        c3: camSite(a.cameraId) || '—',
        status: { label: 'New', color: severityColor(a.severity) },
        time: clock(sec),
        tag: sec ? `${spanLabel(Date.now() / 1000 - sec)} ago` : undefined,
        place: camName(a.cameraId),
        onOpen: () => openAlert(a),
      };
    });

    return (
      <BoardCardBody
        head={head}
        // Cards view: beside the summary it is the timeline rail, read left to right; under it (a
        // vertical card) the same alerts as a tile grid that scrolls down, like every other card.
        details={view === 'cards' && split ? (
          <AlertTimeline
            alerts={filteredAlerts}
            places={alertPlaces}
            loading={latest.loading}
            onOpenAlert={openAlert}
            loadPreview={hexAlerts.loadPreview}
            onLoadMore={latest.hasMore ? latest.loadMore : undefined}
            loadingMore={latest.loadingMore}
          />
        ) : (
          <DetailsPanel
            title="All open alerts"
            count={`${fmt(latest.alerts.length)} shown of ${fmt(open)}`}
            placeholder="Search alerts…"
            columns={['Alert', 'Camera', 'Site', 'Status', 'Time']}
            items={items}
            thumbs
            loading={latest.loading}
            empty={sev === 'all' ? 'No open alerts.' : `No open ${severityLabel(sev).toLowerCase()} alerts.`}
            onLoadMore={latest.hasMore ? latest.loadMore : undefined}
            loadingMore={latest.loadingMore}
          />
        )}
      >
        <SeverityBoxes counts={counts} active={sev} onPick={setSev} />
        <CardSection>
          {pulse && hasSeries(pulse.series) ? (
            <BarsPlot
              series={pulse.series}
              color={TONE.crit}
              caption={`Alerts per 15 min · last ${pulse.hours} h`}
              scale={`peak ${fmt(pulse.peak)}`}
              unit="alerts"
            />
          ) : (
            <Quiet sub="Nothing raised in the last 12 hours.">{core ? 'Quiet' : 'Loading…'}</Quiet>
          )}
        </CardSection>
        <CardFoot>
          {newest
            ? `Latest${sev === 'all' ? '' : ` ${severityLabel(sev).toLowerCase()}`}: ${alertName({ alert_name: newest.alertName, event_type: newest.eventType })} · ${camName(newest.cameraId)}${newestSec ? ` · ${spanLabel(Date.now() / 1000 - newestSec)} ago` : ''}`
            : latest.loading ? 'Loading the live feed…' : 'No open alerts'}
        </CardFoot>
      </BoardCardBody>
    );
  };

  const renderWatch = (subject: 'person' | 'vehicle') => {
    const { range } = useBoardCard();
    const f = figures(range);
    const side = f.watchlist?.[subject];
    const isPerson = subject === 'person';
    const vocab = isPerson ? PERSON_WATCHLIST_CATEGORIES : VEHICLE_WATCHLIST_CATEGORIES;
    const rows = isPerson ? peopleWatchRows : vehicleWatchRows;
    const head = (
      <CardHead
        icon={isPerson ? ShieldExclamationIcon : TruckIcon}
        title={isPerson ? 'People Watch List' : 'Vehicle Watch List'}
        chip={side && side.sightings > 0 ? <Chip>{fmt(side.subjects)} seen</Chip> : undefined}
        onOpen={() => navigate(watchListHref('/watch-list', { tab: 'detections', subject, window: f.window.label || 'today' }))}
        openLabel="Open the watch list"
      />
    );
    if (f.watchlistLoading) return <BoardCardBody head={head}><CardSkeleton /></BoardCardBody>;
    if (!side) {
      return (
        <BoardCardBody head={head}>
          <p className="text-[12px] text-muted-foreground">Couldn't read the watch list figures.</p>
        </BoardCardBody>
      );
    }
    const catKey = (d: WatchlistDetection) => watchlistCategoryOf(vocab, d.category)?.value || 'none';
    const chips = [...groupCounts(rows.items, catKey)].map(([key, count]) => {
      const cat = key === 'none' ? null : watchlistCategoryOf(vocab, key);
      return { key, count, label: cat?.label || 'Not listed', color: (cat && CATEGORY_TONE[cat.value]) || TONE.muted };
    });
    const items: DetailItem[] = newestFirst(rows.items, (d) => d.event_timestamp).map((d) => {
      const cat = watchlistCategoryOf(vocab, d.category);
      const tone = (cat && CATEGORY_TONE[cat.value]) || TONE.muted;
      const key = detectionImageKey(d);
      const ref = key ? d.image_urls?.[key] : null;
      const label = d.subject_label || (isPerson ? 'Unknown person' : 'Unread plate');
      return {
        id: d.event_id,
        title: label,
        sub: d.direction ? d.direction.replace(/_/g, ' ') : d.reason || undefined,
        badge: isPerson
          ? { text: initials(label), color: tone, shape: 'circle' }
          : { text: label.replace(/[^A-Za-z0-9]/g, '').slice(0, 2) || '?', color: tone, shape: 'square' },
        ...(d.event_id && (ref || d.snapshot_path)
          ? host.picture({
            model: isPerson ? 'face' : 'vehicle_crossing',
            eventId: d.event_id,
            key: ref ? key! : 'snapshot',
            ref: ref || d.snapshot_path!,
          })
          : {}),
        c2: camName(d.camera_id),
        c3: isPerson ? (pct(d.confidence) ? `${pct(d.confidence)} match` : '—') : (d.direction || '—'),
        status: { label: cat?.label || d.category || 'Not listed', color: tone },
        time: clock(d.event_timestamp),
        tag: isPerson ? (pct(d.confidence) ? `${pct(d.confidence)} match` : undefined) : d.direction || undefined,
        place: camName(d.camera_id),
        keywords: d.reason || undefined,
        groups: [catKey(d)],
        data: d.event_id
          ? { model: isPerson ? 'face' : 'vehicle_crossing', id: d.event_id, title: label, personName: d.subject_label || undefined }
          : undefined,
      };
    });
    return (
      <BoardCardBody
        head={head}
        details={
          <DetailsPanel
            title={isPerson ? 'All detected watch-list people' : 'All detected watch-list vehicles'}
            count={`${fmt(side.sightings)} ${isPerson ? 'sightings' : 'crossings'} · ${fmt(side.subjects)} ${isPerson ? 'people' : 'vehicles'}`}
            placeholder={isPerson ? 'Search people…' : 'Search plates…'}
            columns={isPerson ? ['Person', 'Camera', 'Confidence', 'List', 'Seen'] : ['Plate', 'Camera', 'Direction', 'Category', 'Seen']}
            items={items}
            loading={rows.loading}
            error={rows.error}
            empty={isPerson ? 'Nobody on the watch list was seen in this window.' : 'No watch-listed plate crossed in this window.'}
            onOpenItem={openEventRow}
          />
        }
      >
        <Hero
          value={fmt(side.sightings)}
          unit={`${isPerson ? (side.sightings === 1 ? 'sighting' : 'sightings') : (side.sightings === 1 ? 'crossing' : 'crossings')} ${windowSuffix(f.window.label)}`}
          tone={side.sightings > 0 ? TONE.crit : undefined}
        />
        <FilterChips chips={chips} />
        <CardSection>
          <BarsOrQuiet
            series={side.series as any}
            total={side.sightings}
            color={TONE.crit}
            caption={`${isPerson ? 'Sightings' : 'Crossings'} ${perBucket(side.series)}`}
            unit={isPerson ? 'sightings' : 'crossings'}
            quiet={side.listed > 0
              ? (isPerson ? 'Nobody on the watch list was seen' : 'No watch-listed plate crossed')
              : (isPerson ? 'No people on the watch list' : 'No vehicles on the watch list')}
          />
        </CardSection>
        <CardFoot>
          {`${windowCaption(f.window.label)} · ${fmt(side.listed)} ${isPerson ? (side.listed === 1 ? 'person' : 'people') : (side.listed === 1 ? 'plate' : 'plates')} on the list`}
        </CardFoot>
      </BoardCardBody>
    );
  };

  const renderPresence = () => {
    const att = core?.attendance;
    const head = (
      <CardHead icon={UserGroupIcon} title="Presence" onOpen={() => navigate('/attendance/presence')} openLabel="Open the presence board" />
    );
    if (!core) return <BoardCardBody head={head}><CardSkeleton /></BoardCardBody>;
    if (!att) {
      return (
        <BoardCardBody head={head}>
          <p className="text-[12px] text-muted-foreground">Couldn't read today's presence.</p>
        </BoardCardBody>
      );
    }
    // Most recently present first: on site now (latest arrival first), then those who came and
    // left (latest departure first), then the absent by name.
    const rank = (e: any) => (e.is_inside ? 2 : (e.status || 'absent') === 'absent' ? 0 : 1);
    const lastSeen = (e: any) => sortTime(e.is_inside ? e.first_in_at : e.last_out_at || e.first_in_at);
    const ordered = [...presenceRows.items].sort((a: any, b: any) =>
      rank(b) - rank(a) || lastSeen(b) - lastSeen(a) || String(a.name || '').localeCompare(String(b.name || '')));
    const items: DetailItem[] = ordered.map((e: any) => {
      const inside = !!e.is_inside;
      const absent = (e.status || 'absent') === 'absent';
      const short = e.status === 'half_day' || e.status === 'partial';
      const status = inside
        ? { label: 'On site', color: TONE.ok }
        : absent
          ? { label: 'Absent', color: TONE.muted }
          : short
            ? { label: 'Short day', color: TONE.warn }
            : { label: 'Left', color: TONE.info };
      const out = isoClock(e.last_out_at);
      return {
        id: e.id,
        title: e.name,
        sub: [e.department || e.designation, e.employee_code].filter(Boolean).join(' · ') || undefined,
        badge: { text: initials(e.name), color: absent ? '#94a3b8' : badgeColor(e.name || e.id), shape: 'circle' },
        loadImage: e.primary_photo_id ? () => loadEnrolledPhoto(attendanceApi, e.primary_photo_id) : undefined,
        c2: e.designation || e.department || '—',
        c3: inside ? 'Inside' : absent ? '—' : out ? `Out ${out}` : 'Out',
        status,
        time: isoClock(e.first_in_at) || '—',
        tag: e.total_inside_minutes ? `${Math.floor(e.total_inside_minutes / 60)}h ${e.total_inside_minutes % 60}m` : undefined,
        place: e.department || undefined,
        groups: absent ? ['absent'] : [inside ? 'inside' : 'left', 'present', ...(short ? ['short'] : [])],
      };
    });
    return (
      <BoardCardBody
        head={head}
        details={
          <DetailsPanel
            title="All employees today"
            count={`${fmt(att.present)} present · ${fmt(att.absent)} absent`}
            placeholder="Search employees…"
            columns={['Employee', 'Role', 'In / Out', 'Status', 'First in']}
            items={items}
            loading={presenceRows.loading}
            error={presenceRows.error}
            empty="No active employees."
            onOpenItem={openEmployeeRow(att.work_date)}
          />
        }
      >
        <Hero value={fmt(att.inside)} unit="on site" tone={TONE.brand} filterKey="inside" />
        <CardSection>
          <RingStat
            percent={att.total > 0 ? (att.present / att.total) * 100 : 0}
            caption="present"
            color={TONE.brand}
            ariaLabel={`${att.present} of ${att.total} employees present today`}
            legend={[
              { key: 'present', label: 'Present today', value: `${fmt(att.present)} / ${fmt(att.total)}`, color: TONE.brand },
              { key: 'absent', label: 'Absent', value: fmt(att.absent), color: TONE.muted },
              { key: 'short', label: 'Short day', value: fmt(att.short_day), color: TONE.warn },
            ]}
          />
        </CardSection>
        <CardFoot>{`Presence for work day ${att.work_date}`}</CardFoot>
      </BoardCardBody>
    );
  };

  /** Shared shell for the per-model cards: loading / failed states, then `body`. */
  const surfaceState = (key: string) => {
    const { range } = useBoardCard(); // eslint-disable-line react-hooks/rules-of-hooks
    const f = figures(range);
    return { f, s: f.surfaces?.[key] ?? null, loading: f.surfacesLoading };
  };

  const renderPersonCount = () => {
    const { f, s, loading } = surfaceState('person-count');
    const head = <CardHead icon={UsersIcon} title="Person Count" onOpen={go('/reports/person-count')} openLabel="Open the Person Count report" />;
    if (loading) return <BoardCardBody head={head}><CardSkeleton /></BoardCardBody>;
    if (!s) return <BoardCardBody head={head}><p className="text-[12px] text-muted-foreground">Couldn't read the figures.</p></BoardCardBody>;
    const cams = [...(s.by_camera || [])].sort((a, b) => b.now - a.now || b.peak - a.peak);
    const occupied = cams.filter((c) => c.now > 0).length;
    const items: DetailItem[] = cams.map((c) => ({
      id: c.camera_id,
      title: camName(c.camera_id),
      sub: 'Counting camera',
      badge: { text: String(c.now), color: badgeColor(c.camera_id), shape: 'square' },
      loadImage: () => latestCameraFrame(host, 'person_count', c.camera_id),
      c2: camSite(c.camera_id) || '—',
      c3: `peak ${fmt(c.peak)}`,
      status: { label: 'Counting', color: TONE.ok },
      time: `now ${fmt(c.now)}`,
      tag: `peak ${fmt(c.peak)}`,
      place: camName(c.camera_id),
      groups: [c.now > 0 ? 'occupied' : 'empty'],
      data: { model: 'person_count', cameraId: c.camera_id, title: camName(c.camera_id) },
    }));
    return (
      <BoardCardBody
        head={head}
        details={
          <DetailsPanel
            title="All counting cameras"
            count={`${fmt(s.reporting)} ${s.reporting === 1 ? 'camera' : 'cameras'} reporting`}
            placeholder="Search cameras…"
            columns={['Camera', 'Site', 'Peak', 'Status', 'Now']}
            items={items}
            empty="No camera has reported a count in the last few minutes."
            onOpenItem={openEventRow}
          />
        }
      >
        <Hero value={fmt(s.now)} unit="on site now" tone={TONE.brand} />
        <FilterChips
          chips={[
            { key: 'occupied', label: 'People in view', count: occupied, color: TONE.brand },
            { key: 'empty', label: 'Empty', count: cams.length - occupied, color: TONE.muted },
          ]}
        />
        <CardSection>
          <BarsOrQuiet
            series={s.series as any}
            total={hasSeries(s.series) ? seriesMax(s.series) : 0}
            color={TONE.brand}
            caption={`Site total, highest ${perBucket(s.series).replace('per ', 'each ')}`}
            unit="people"
            quiet="Nobody counted on site"
          />
        </CardSection>
        <CardFoot>{`${windowCaption(f.window.label)} · ${fmt(s.reporting)} ${s.reporting === 1 ? 'camera' : 'cameras'} counting`}</CardFoot>
      </BoardCardBody>
    );
  };

  const renderFaces = () => {
    const { f, s, loading } = surfaceState('face');
    const head = <CardHead icon={FaceSmileIcon} title="Faces Recognised" onOpen={go('/reports/face')} openLabel="Open the Face report" />;
    if (loading) return <BoardCardBody head={head}><CardSkeleton /></BoardCardBody>;
    if (!s) return <BoardCardBody head={head}><p className="text-[12px] text-muted-foreground">Couldn't read the figures.</p></BoardCardBody>;
    const known = Number(s.known || 0);
    const unknown = Number(s.unknown || 0);
    const seen = known + unknown;
    const items: DetailItem[] = newestFirst(faceRows.items, (e: any) => e.event_timestamp).map((e: any) => {
      const isKnown = !!e.is_known;
      const name = isKnown ? e.matched_person_name || 'Registered person' : 'Unrecognised';
      const match = pct(e.similarity_score);
      return {
        id: e.id || e.event_id,
        title: name,
        sub: e.direction ? String(e.direction).replace(/_/g, ' ') : undefined,
        badge: { text: isKnown ? initials(name) : '?', color: isKnown ? badgeColor(name) : '#94a3b8', shape: 'circle' },
        ...eventPicture(host, e, 'face', 'face'),
        c2: camName(e.camera_id),
        c3: match ? `${match} match` : '—',
        status: isKnown ? { label: 'Known', color: TONE.ok } : { label: 'Unknown', color: TONE.warn },
        time: clock(Number(e.event_timestamp)),
        tag: match ? `${match} match` : undefined,
        place: camName(e.camera_id),
        groups: [isKnown ? 'known' : 'unknown'],
        data: { model: 'face', id: String(e.event_id || e.id), title: name, personName: isKnown ? name : undefined },
      };
    });
    return (
      <BoardCardBody
        head={head}
        details={
          <DetailsPanel
            title="All recognitions"
            count={`${fmt(seen)} detections · ${fmt(s.subjects)} people`}
            placeholder="Search people…"
            columns={['Person', 'Camera', 'Match', 'Status', 'Seen']}
            items={items}
            loading={faceRows.loading}
            error={faceRows.error}
            empty="No faces in this window."
            onOpenItem={openEventRow}
          />
        }
      >
        <Hero value={fmt(s.subjects)} unit={`${s.subjects === 1 ? 'person' : 'people'} recognised`} />
        <CardSection>
          <RingStat
            percent={seen > 0 ? (known / seen) * 100 : 0}
            caption="matched"
            color={TONE.ok}
            ariaLabel={`${known} of ${seen} face detections matched a registered person`}
            legend={[
              { key: 'known', label: 'Known', value: fmt(known), color: TONE.ok },
              { key: 'unknown', label: 'Unrecognised', value: fmt(unknown), color: TONE.warn },
            ]}
          />
        </CardSection>
        <CardFoot>{`${windowCaption(f.window.label)} · ${fmt(seen)} face detections`}</CardFoot>
      </BoardCardBody>
    );
  };

  const renderPeopleDetected = () => {
    const { f, s, loading } = surfaceState('person');
    const head = <CardHead icon={ViewfinderCircleIcon} title="People Detected" onOpen={go('/reports/person')} openLabel="Open the Person report" />;
    if (loading) return <BoardCardBody head={head}><CardSkeleton /></BoardCardBody>;
    if (!s) return <BoardCardBody head={head}><p className="text-[12px] text-muted-foreground">Couldn't read the figures.</p></BoardCardBody>;
    const total = detectionRows.items.reduce((acc, r) => acc + r.count, 0) || 1;
    const items: DetailItem[] = detectionRows.items.map((r) => ({
      id: r.camera_id,
      title: camName(r.camera_id),
      sub: 'Person detection',
      badge: { text: r.count >= 1000 ? `${Math.round(r.count / 1000)}k` : String(r.count), color: TONE.info, shape: 'square' },
      loadImage: () => latestCameraFrame(host, 'person', r.camera_id),
      c2: camSite(r.camera_id) || '—',
      c3: `${Math.round((r.count / total) * 100)}% of all`,
      status: { label: r.count > 0 ? 'Detecting' : 'Quiet', color: r.count > 0 ? TONE.info : TONE.muted },
      time: fmt(r.count),
      tag: `${fmt(r.count)} detections`,
      place: camName(r.camera_id),
      data: { model: 'person', cameraId: r.camera_id, title: camName(r.camera_id) },
    }));
    return (
      <BoardCardBody
        head={head}
        details={
          <DetailsPanel
            title="Detections by camera"
            count={`${fmt(s.events)} detections · top ${items.length} cameras`}
            placeholder="Search cameras…"
            columns={['Camera', 'Site', 'Share', 'Status', 'Detections']}
            items={items}
            loading={detectionRows.loading}
            error={detectionRows.error}
            empty="No detections in this window."
            onOpenItem={openEventRow}
          />
        }
      >
        <Hero value={fmt(s.events)} unit={`detections ${windowSuffix(f.window.label)}`} hint="Sightings, not people — one person may be detected many times." />
        <CardSection>
          <BarsOrQuiet
            series={s.series as any}
            total={s.events}
            color={TONE.info}
            emphasis="last"
            caption={`Detections ${perBucket(s.series)}`}
            unit="detections"
            quiet="Nothing detected"
          />
        </CardSection>
        <CardFoot>Sightings, not people — one person may be detected many times.</CardFoot>
      </BoardCardBody>
    );
  };

  const renderDetector = (surface: string) => {
    const meta = DETECTOR_CARDS[surface];
    const { f, s, loading } = surfaceState(surface);
    const head = <CardHead icon={meta.icon} title={meta.title} onOpen={go(meta.report)} openLabel={`Open the ${meta.title} report`} />;
    if (loading) return <BoardCardBody head={head}><CardSkeleton /></BoardCardBody>;
    if (!s) return <BoardCardBody head={head}><p className="text-[12px] text-muted-foreground">Couldn't read the figures.</p></BoardCardBody>;
    const rows = detectorRows[surface];
    const whatOf = (e: any) =>
      String(e.plate_number || e.license_plate || e.violation_type || e.detection_type || e.sub_type || e.zone_name || e.roi_zone || meta.title).replace(/_/g, ' ');
    // By kind of event, not by plate — a plate list would be one chip per vehicle.
    const kindOf = (e: any) =>
      String(e.violation_type || e.detection_type || e.sub_type || e.zone_name || e.roi_zone || meta.title).replace(/_/g, ' ');
    const chips = [...groupCounts(rows.items, kindOf)].map(([key, count]) => ({ key, label: key, count, color: meta.tone }));
    const items: DetailItem[] = newestFirst(rows.items, (e: any) => e.event_timestamp).map((e: any) => {
      return {
        id: e.id || e.event_id,
        title: whatOf(e),
        sub: e.direction ? String(e.direction).replace(/_/g, ' ') : undefined,
        ...eventPicture(host, e, modelKey(surface)),
        c2: camName(e.camera_id),
        c3: pct(e.confidence) ? `${pct(e.confidence)} conf.` : '—',
        status: { label: 'Detected', color: meta.tone },
        time: clock(Number(e.event_timestamp)),
        tag: pct(e.confidence) ? `${pct(e.confidence)}` : undefined,
        place: camName(e.camera_id),
        groups: [kindOf(e)],
        data: { model: modelKey(surface), id: String(e.event_id || e.id), title: whatOf(e) },
      };
    });
    return (
      <BoardCardBody
        head={head}
        details={
          <DetailsPanel
            title={`All ${meta.title.toLowerCase()}`}
            count={`${fmt(s.events)} ${meta.unit}`}
            columns={['Event', 'Camera', 'Confidence', 'Status', 'Time']}
            items={items}
            loading={rows.loading}
            error={rows.error}
            empty="Nothing in this window."
            onOpenItem={openEventRow}
          />
        }
      >
        <Hero value={fmt(s.events)} unit={`${meta.unit} ${windowSuffix(f.window.label)}`} tone={s.events > 0 && meta.tone !== TONE.brand ? meta.tone : undefined} />
        <FilterChips chips={chips} />
        <CardSection>
          <BarsOrQuiet
            series={s.series as any}
            total={s.events}
            color={meta.tone}
            caption={`${meta.unit[0].toUpperCase()}${meta.unit.slice(1)} ${perBucket(s.series)}`}
            unit={meta.unit}
            quiet={meta.quiet}
          />
        </CardSection>
        <CardFoot>{windowCaption(f.window.label)}</CardFoot>
      </BoardCardBody>
    );
  };

  const renderSystem = () => {
    const { size, split } = useBoardCard();
    const camFilter = useCardFilter();
    const m: any = data.metrics || {};
    const cams = data.cameras || [];
    const bucket = (c: any) =>
      c.status === 'streaming' || c.status === 'recording'
        ? 'online'
        : c.status === 'connecting'
          ? 'connecting'
          : c.status === 'error'
            ? 'error'
            : 'offline';
    const counts = { online: 0, connecting: 0, error: 0, offline: 0 };
    cams.forEach((c: any) => { counts[bucket(c)] += 1; });
    const head = (
      <CardHead
        icon={ServerStackIcon}
        title="System Performance"
        caption={size === 'expand' && !split ? statusNote || `This ${host.subjectNoun} — live load, cameras and AI models` : undefined}
        onOpen={canMonitor && host.monitorPath ? () => navigate(host.monitorPath!) : undefined}
        openLabel="Open System Health"
      />
    );
    const statusTone: Record<string, { label: string; color: string }> = {
      online: { label: 'Online', color: TONE.ok },
      connecting: { label: 'Connecting', color: TONE.warn },
      error: { label: 'Error', color: TONE.crit },
      offline: { label: 'Offline', color: TONE.muted },
    };
    const items: DetailItem[] = cams.map((c: any, i: number) => {
      const b = bucket(c);
      return {
        id: c.id,
        title: c.name,
        sub: [c.ip_address, [c.manufacturer, c.model].filter(Boolean).join(' ')].filter(Boolean).join(' · ') || undefined,
        badge: { text: `C${i + 1}`, color: b === 'online' ? '#334155' : '#94a3b8', shape: 'square' },
        c2: camSite(c.id) || c.location || '—',
        c3: c.is_recording ? 'Recording' : c.recording_enabled === false ? 'Off' : '—',
        status: statusTone[b],
        time: c.is_recording ? 'REC' : '',
        tag: c.is_recording ? 'Recording' : undefined,
        place: c.name,
        onOpen: canCameras ? () => navigate('/camera-management') : undefined,
        groups: [b],
      };
    });
    const meters = (
      <div className="grid flex-none grid-cols-3 gap-3">
        <Meter label="CPU" percent={m.cpuPercent} sub="Busiest machine" />
        <Meter label="Memory" percent={m.memoryPercent} />
        <Meter label="Disk" percent={m.storageTotalGB ? (m.storageUsedGB / m.storageTotalGB) * 100 : null} />
      </div>
    );

    const cameraList = (
      <DetailsPanel
        title="All cameras"
        count={`${fmt(cams.length)} cameras · ${fmt(counts.online)} online`}
        placeholder="Search cameras…"
        columns={['Camera', 'Site', 'Recording', 'Status', '']}
        items={items}
        loading={data.loading}
        empty={canCameras ? 'No cameras on this node.' : 'You do not have access to the camera list.'}
      />
    );

    return (
      <BoardCardBody head={head} details={cameraList}>
        {data.loading ? (
          <CardSkeleton />
        ) : (
          <>
            {canCameras && (
              <div className="flex min-h-0 flex-1 flex-col justify-center">
                <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Cameras</p>
                <CameraStatusDonut
                  total={cams.length}
                  onPick={camFilter.filterable ? camFilter.pick : undefined}
                  active={camFilter.active}
                  slices={[
                    { key: 'online', label: 'Online', value: counts.online, color: TONE.ok },
                    { key: 'connecting', label: 'Connecting', value: counts.connecting, color: TONE.warn },
                    { key: 'error', label: 'Error', value: counts.error, color: TONE.crit },
                    { key: 'offline', label: 'Offline', value: counts.offline, color: TONE.muted },
                  ]}
                />
              </div>
            )}
            {canMonitor && <div className="border-t border-border pt-2.5">{meters}</div>}
            <CardFoot>
              {[statusNote || `This ${host.subjectNoun}`, m.uptimeSeconds ? `up ${formatUptime(m.uptimeSeconds)}` : null].filter(Boolean).join(' · ')}
            </CardFoot>
          </>
        )}
      </BoardCardBody>
    );
  };

  // ── Card definitions ──

  const RANGED = true;
  const cards: BoardCardDef[] = [
    {
      id: 'alerts',
      title: 'Alert Timeline',
      icon: BellAlertIcon,
      render: renderAlerts,
    },
    { id: 'peopleWatch', title: 'People Watch List', icon: ShieldExclamationIcon, ranged: RANGED, render: () => renderWatch('person') },
    { id: 'vehicleWatch', title: 'Vehicle Watch List', icon: TruckIcon, ranged: RANGED, render: () => renderWatch('vehicle') },
    { id: 'presence', title: 'Presence', icon: UserGroupIcon, render: renderPresence },
    { id: 'personCount', title: 'Person Count', icon: UsersIcon, ranged: RANGED, render: renderPersonCount },
    { id: 'faces', title: 'Faces Recognised', icon: FaceSmileIcon, ranged: RANGED, render: renderFaces },
    { id: 'peopleDetected', title: 'People Detected', icon: UserIcon, ranged: RANGED, render: renderPeopleDetected },
    { id: 'system', title: 'System Performance', icon: ServerStackIcon, render: renderSystem },
    ...Object.entries(DETECTOR_CARDS).map(([key, meta]) => ({
      id: `surface:${key}`,
      title: meta.title,
      icon: meta.icon,
      ranged: RANGED,
      render: () => renderDetector(key),
    })),
  ];

  const nothingPermitted = catalog.length === 0;

  return (
    <>
      <div className="space-y-[14px]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-[22px] font-bold tracking-[-0.01em] text-foreground">{host.title}</h1>
            {host.subtitle && <p className="mt-[3px] text-[13px] text-muted-foreground">{host.subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <button
                  type="button"
                  onClick={layout.reset}
                  className={`flex h-[38px] items-center rounded-[10px] border border-border bg-card px-3.5 text-[13px] font-medium text-foreground transition-colors hover:border-brand hover:text-brand ${FOCUS}`}
                >
                  Reset to default
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className={`flex h-[38px] items-center gap-1.5 rounded-[10px] bg-brand px-4 text-[13px] font-semibold text-white transition-colors hover:bg-brand-dark ${FOCUS}`}
                >
                  <CheckIcon className="h-4 w-4" /> Done
                </button>
              </>
            ) : (
              !nothingPermitted && (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className={`flex h-[38px] items-center gap-2 rounded-[10px] border border-border bg-card px-3.5 text-[13px] font-semibold text-foreground transition-colors hover:border-brand hover:text-brand ${FOCUS}`}
                >
                  <Squares2X2Icon className="h-4 w-4" /> Customize layout
                </button>
              )
            )}
            <button
              type="button"
              onClick={refreshAll}
              title="Refresh"
              aria-label="Refresh"
              className={`flex h-[38px] w-[38px] items-center justify-center rounded-[10px] border border-border bg-card text-muted-foreground transition-colors hover:border-brand hover:text-brand ${FOCUS}`}
            >
              <ArrowPathIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {failed.length > 0 && (
          <div
            className="rounded-lg border px-3 py-2 text-[12px] text-destructive"
            style={{ borderColor: tint(TONE.crit, 40), backgroundColor: tint(TONE.crit, 10) }}
          >
            Couldn't read {failed.join(', ')} — those cards may be empty or out of date.
          </div>
        )}

        {nothingPermitted ? (
          <div className="rounded-2xl border border-border bg-card p-5 text-[13px] text-muted-foreground shadow-card">
            You don't have access to any of {`this ${host.subjectNoun}'s`} summaries.
          </div>
        ) : (
          <DashboardBoard cards={cards} layout={layout} editing={editing} />
        )}

        {surfaces === null && canAiEvents && (
          <p className="text-[11px] text-muted-foreground">
            Model list unavailable — per-model cards are hidden until it can be read.
          </p>
        )}
      </div>

      {/* The alert's record, over the dashboard — the same modal the alert board opens. */}
      <Modal
        open={!!viewing}
        onClose={() => setViewingIndex(null)}
        title={viewing && (
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-brand">{viewing.alertName || 'Alert'}</span>
            <span className="text-[13px] font-normal text-muted-foreground">· Alert Details</span>
          </span>
        )}
        ariaLabel="Alert details"
        subtitle={viewing ? alertPlaces[viewing.cameraId]?.name : undefined}
        size="full"
        headerActions={viewing && viewingIndex != null && (
          <>
            <RecordPager
              index={Math.min(viewingIndex, viewable.length - 1)}
              count={viewable.length}
              onIndexChange={setViewingIndex}
              noun="alerts"
            />
            <button
              type="button"
              onClick={() => navigate(`/alerts/${viewing.id}`)}
              title="Open the full alert page"
              className="flex h-8 items-center gap-1.5 rounded-lg border border-border px-2.5 text-[12px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" /> Full page
            </button>
          </>
        )}
      >
        {viewing?.id && <host.AlertRecord key={viewing.id} alertId={viewing.id} />}
      </Modal>

      {/* A presence row's person — the presence board's drawer contents, in this page's frame. */}
      <Modal
        open={!!viewingEmployee}
        onClose={() => setEmployeeView(null)}
        title={viewingEmployee && (
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-brand">{viewingEmployee.name}</span>
            <span className="text-[13px] font-normal text-muted-foreground">· Presence</span>
          </span>
        )}
        ariaLabel="Employee presence details"
        subtitle={viewingEmployee?.sub}
        size="full"
        headerActions={viewingEmployee && employeeView && (
          <>
            <RecordPager
              index={employeeView.index}
              count={employeeView.rows.length}
              onIndexChange={(index) => setEmployeeView((cur) => (cur ? { ...cur, index } : cur))}
              noun="employees"
            />
            <button
              type="button"
              onClick={() => navigate('/attendance/presence')}
              title="Open the presence board"
              className="flex h-8 items-center gap-1.5 rounded-lg border border-border px-2.5 text-[12px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" /> Presence board
            </button>
          </>
        )}
      >
        {viewingEmployee && (
          <div className="p-4 sm:p-6">
            <EmployeeDetailBody
              key={viewingEmployee.id}
              employeeId={viewingEmployee.id}
              initialDate={employeeView?.date}
              showHeader={false}
            />
          </div>
        )}
      </Modal>

      {/* A details row's event, over the dashboard — the same record the reports open. */}
      <Modal
        open={!!viewingEvent}
        onClose={() => setEventView(null)}
        title={viewingEvent && (
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-brand">{viewingEvent.title}</span>
            <span className="text-[13px] font-normal text-muted-foreground">
              · {reportModelLabel(viewingEvent.model)}{viewingEvent.id ? '' : ' · latest event'}
            </span>
          </span>
        )}
        ariaLabel="Event details"
        size="full"
        headerActions={viewingEvent && eventView && (
          <>
            <RecordPager
              index={eventView.index}
              count={eventView.refs.length}
              onIndexChange={(index) => { setEventViewId(null); setEventView((cur) => (cur ? { ...cur, index } : cur)); }}
              noun="events"
            />
            {eventViewId && (
              <button
                type="button"
                onClick={() => navigate(`/events/${reportModelSlug(viewingEvent.model)}/${encodeURIComponent(eventViewId)}`)}
                title="Open the full event page"
                className="flex h-8 items-center gap-1.5 rounded-lg border border-border px-2.5 text-[12px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" /> Full page
              </button>
            )}
          </>
        )}
      >
        {viewingEvent && (
          <DashboardEventRecord
            host={host}
            key={`${viewingEvent.model}:${viewingEvent.id || viewingEvent.cameraId}`}
            eventRef={viewingEvent}
            cameraNameMap={cameraNameMap}
            onLoaded={setEventViewId}
          />
        )}
      </Modal>
    </>
  );
}

